import React, { useRef, useEffect } from 'react';

/**
 * SynchronizedVideo – a <video> element that follows the shared playback state
 * coming from the parent and notifies the parent when the user interacts with it.
 *
 * Props
 *  - src            : string   video URL
 *  - currentTime    : number   shared position (seconds)
 *  - playing        : boolean  shared playing flag
 *  - onUserSeek     : (t)=>void
 *  - onUserPlayPause: (playing:boolean)=>void
 *  - offset         : number   offset for global time calculation
 *  - globalTime     : number   global time reference
 *  - videoId        : string   unique identifier for debugging
 */
export default function SynchronizedVideo({ src, currentTime, playing, onUserSeek, onUserPlayPause, offset = 0, globalTime = 0, videoId }) {
  const ref = useRef(null);
  const programmaticChangeRef = useRef(false); // Flag to prevent feedback loops
  const isSeekingRef = useRef(false); // Track if user is currently seeking
  const programmaticSeekRef = useRef(false); // Track if we just programmatically set currentTime
  const hasPlayedRef = useRef(false); // Track if video has ever been played
  const lastSyncTimeRef = useRef(0); // Track when we last synced to prevent rapid seeks

  // Sync position changes coming from parent
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const delta = Math.abs(v.currentTime - currentTime);
    const now = Date.now();
    
    // Use a larger threshold to avoid constant micro-adjustments
    // Also throttle syncs to prevent rapid seeks
    if (delta > 0.2 && (now - lastSyncTimeRef.current) > 150) {
      lastSyncTimeRef.current = now;
      // Set flag to indicate this is a programmatic seek
      programmaticSeekRef.current = true;
      v.currentTime = currentTime;
      // Reset flag after a longer delay to ensure all events are captured
      setTimeout(() => {
        programmaticSeekRef.current = false;
      }, 300);
    }
  }, [currentTime]);

  // Sync play / pause coming from parent
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    programmaticChangeRef.current = true; // Set flag before programmatic changes
    if (playing && v.paused) {
      hasPlayedRef.current = true; // Mark that video has been played
      const p = v.play();
      if (p && p.catch) {
        p.catch((err) => {
          console.warn('[VideoSync] Play failed (likely autoplay restriction):', err);
          // Don't propagate play failures as pause events
        });
      }
    }
    if (!playing && !v.paused) {
      v.pause();
    }
    // Reset flag after a longer delay to ensure all events are captured
    setTimeout(() => {
      programmaticChangeRef.current = false;
    }, 200);
  }, [playing]);

  // Debug logging on every timeupdate (manageable frequency)
  const debugLog = (label, extra={}) => {
    console.log(`[VideoSync] ${label}`, {
      videoId,
      src,
      videoCurrent: ref.current?.currentTime ?? null,
      offset,
      globalTime,
      calcGlobal: (ref.current?.currentTime ?? 0) + offset,
      hasPlayed: hasPlayedRef.current,
      ...extra,
    });
  };

  const handlePlay = () => {
    debugLog('play', { programmaticChange: programmaticChangeRef.current });
    hasPlayedRef.current = true; // Mark that video has been played by user
    // NEVER let automatic video play events control the master playback
    // Only user clicks on the video itself should control playback
    debugLog('play ignored - videos should not control master playback');
  };
  const handlePause = () => {
    debugLog('pause', { 
      programmaticChange: programmaticChangeRef.current,
      hasPlayed: hasPlayedRef.current 
    });
    // NEVER let video pause events stop the master playback
    // Videos should be passive followers, not controllers
    // Only user clicks on the video itself should control playback
    debugLog('pause ignored - videos should not control master playback');
  };
  const handleTimeUpdate = () => {
    // Videos should NOT drive time updates during normal playback
    // This prevents feedback loops - only the master timer should drive time
    debugLog('timeupdate', { note: 'Video timeupdate ignored to prevent feedback loops' });
  };
  const handleSeeking = () => {
    debugLog('seeking', { 
      programmaticSeek: programmaticSeekRef.current,
      hasPlayed: hasPlayedRef.current 
    });
    isSeekingRef.current = true;
    
    // Don't trigger onUserSeek during seeking - wait for seeked event
    // This prevents feedback loops during scrubbing
  };
  const handleSeeked = () => {
    debugLog('seeked', { 
      programmaticSeek: programmaticSeekRef.current,
      hasPlayed: hasPlayedRef.current 
    });
    // Reset seeking flag
    isSeekingRef.current = false;
    
    // Only call onUserSeek if:
    // 1. This is NOT a programmatic seek from master timer  
    // 2. The video has been played at least once (prevents initial load seeking)
    // 3. The seek resulted in a significant time change (user actually moved the scrubber)
    if (!programmaticSeekRef.current && hasPlayedRef.current) {
      if (ref.current) {
        const videoTime = ref.current.currentTime;
        const expectedTime = currentTime;
        const delta = Math.abs(videoTime - expectedTime);
        
        // Only notify if the user actually moved to a different position
        // Small deltas indicate automatic synchronization, not user interaction
        if (delta > 0.5) {
          const globalTime = videoTime + offset;
          debugLog('user seek detected', { 
            videoTime, 
            expectedTime, 
            delta, 
            globalTime 
          });
          onUserSeek(globalTime);
        } else {
          debugLog('sync seek ignored', { 
            videoTime, 
            expectedTime, 
            delta 
          });
        }
      }
    }
  };

  const handleClickToggle = () => {
    if (ref.current) {
      if (ref.current.paused) {
        hasPlayedRef.current = true; // Mark as played when user clicks
        ref.current.play().catch(()=>{});
        debugLog('user clicked play - notifying parent');
        onUserPlayPause(true);
      } else {
        ref.current.pause();
        debugLog('user clicked pause - notifying parent');
        onUserPlayPause(false);
      }
    }
  };

  return (
    <video
      src={src}
      ref={ref}
      style={{ width: '100%', maxHeight: '300px', objectFit: 'contain', cursor:'pointer' }}
      onClick={handleClickToggle}
      onPlay={handlePlay}
      onPause={handlePause}
      onSeeking={handleSeeking}
      onSeeked={handleSeeked}
      onTimeUpdate={handleTimeUpdate}
    />
  );
} 